/**
 * Pure tour selection rules, shared by the provider and `useTour`. No DOM, no React.
 *
 * The same rules run on mobile; keep the two in step (see the plan's "Client step filtering").
 */
import type {
  Tour,
  TourProgress,
  TourStep,
  TourStepInteraction,
} from "../../types/tours";
import {
  canAutoPressAnchor,
  COACH_PRESS_ANCHORS,
  SKIP_WHEN_ABSENT_ANCHORS,
  SKIP_WHEN_FOLDED_ANCHORS,
} from "./tourAnchors";

/**
 * Every interaction this build has behaviour for.
 *
 * `satisfies Record<TourStepInteraction, true>` is the point of the shape: adding a value to
 * `TourStepInteraction` fails to compile here until the behaviour for it is acknowledged, which a
 * plain array of strings would not have caught. One copy only — the response guard in
 * `services/tours.ts` narrows with the same predicate rather than keeping a second list that can
 * drift from this one.
 */
const STEP_INTERACTIONS = {
  None: true,
  ClickToContinue: true,
  OpenAnchor: true,
} satisfies Record<TourStepInteraction, true>;

/**
 * Whether a value from the API is an interaction this build knows. Not redundant with the type: a
 * step can arrive from a newer API carrying a value this client has never heard of.
 */
export const isTourStepInteraction = (value: unknown): value is TourStepInteraction =>
  typeof value === "string" && Object.hasOwn(STEP_INTERACTIONS, value);

/**
 * What a step asks the coach to do, defaulted safely.
 *
 * Absent (an API older than the field) and anything this build does not recognise both read as
 * "None", which is the behaviour every step had before interactive steps existed: read it, press
 * Next.
 */
export const stepInteraction = (step: TourStep): TourStepInteraction =>
  isTourStepInteraction(step.interaction) ? step.interaction : "None";

/**
 * Whether `step` opens a popup the steps after it stand inside: a `ClickToContinue` step on a control
 * the tour may press (`AUTO_PRESS_ANCHORS`). The orphaned sub-step rule in `tourDom` and the "new
 * steps" run below both read it.
 */
export const isPopupOpenerStep = (step: TourStep): boolean =>
  step.anchor !== null &&
  stepInteraction(step) === "ClickToContinue" &&
  canAutoPressAnchor(step.anchor);

/** Whether `step` asks the coach to commit a modal's form (Attach, Save), which closes that modal. */
const isCommitStep = (step: TourStep): boolean =>
  step.anchor !== null &&
  stepInteraction(step) === "ClickToContinue" &&
  COACH_PRESS_ANCHORS.has(step.anchor);

/**
 * The unseen steps of a "new steps" run, each brought in with the steps that open the popup it
 * stands inside.
 *
 * A step added inside the player editor would otherwise arrive on its own, pointing at a form that
 * is not open, and the orphaned sub-step rule would skip it or show it as a card. So for each unseen
 * step the run also takes every earlier step back to, and including, the nearest popup opener
 * (`isPopupOpenerStep`). The walk stops without taking anything when it reaches a commit step first
 * (that popup closed before this step) or the start of the tour (the step is on the page itself). An
 * unseen opener needs nothing before it.
 *
 * One level only: an opener that itself sits inside another popup (Customize Tags in the filter modal)
 * is not followed back to that popup's opener. Without the page to ask, there is no telling that
 * opener from one on the page.
 */
export const stepsWithTheirOpeners = (
  steps: readonly TourStep[],
  unseen: ReadonlySet<string>
): TourStep[] => {
  const include = new Set<number>();
  steps.forEach((step, index) => {
    if (!unseen.has(step.key)) return;
    include.add(index);
    if (isPopupOpenerStep(step)) return;
    for (let earlier = index - 1; earlier >= 0; earlier -= 1) {
      const candidate = steps[earlier];
      if (isCommitStep(candidate)) return;
      if (isPopupOpenerStep(candidate)) {
        for (let taken = earlier; taken < index; taken += 1) include.add(taken);
        return;
      }
    }
  });
  return steps.filter((_, index) => include.has(index));
};

export interface RenderContext {
  /** Anchor ids this client build knows how to find. */
  knownAnchors: ReadonlySet<string>;
  /**
   * The user's page permissions. `undefined` means unrestricted: the field is absent for an older
   * API, and admins are unrestricted (see `permissionsForTours`).
   */
  permissions?: readonly string[];
}

/** "full" is a first run; "new" shows only steps added since the user last saw the tour. */
export type TourRunMode = "full" | "new";

export interface TourRun {
  tour: Tour;
  steps: TourStep[];
  mode: TourRunMode;
  /** A help-button replay. Records nothing. */
  replay: boolean;
  /** A system admin previewing a draft. Records nothing, and chains nothing after it. */
  preview?: boolean;
  /**
   * A Guided tours replay of an entry that names no data tour (`dataTourKey: null`). Chains nothing
   * after it: Players has "Player Profiles & Shortcuts" as an entry of its own, and running it straight after
   * step 14 of 14 as "Step 1 of 6" read as the same tour starting over.
   */
  standalone?: boolean;
  /**
   * Started from the Help Center's Guided tours tab: where Finish and Skip take the coach once
   * nothing else follows on the page. A run that carries on into another (a chained data tour, a
   * `TOUR_CONTINUATIONS` entry with `carriesReturn`) hands it on, so the coach goes back only at the
   * very end of the walkthrough.
   */
  returnTo?: string;
  /**
   * How many steps of the same walkthrough were shown before this run (`TOUR_CONTINUATIONS` with
   * `numbered`), so the counter reads "Step 3 of 5" rather than starting again at 1.
   */
  stepOffset?: number;
}

/**
 * Tours that carry on into another as one walkthrough. Lanie's 2026-10-31 review: "Finish", then a
 * second tour starting at "Step 1 of 3", reads as the tour starting over.
 *
 * - `via: "chain"`: the next tour is this page tour's own data tour, chained by the provider when
 *   the page has data (`chainedDataRun`). Skip skips both.
 * - `via: "flow"`: the next tour starts when the coach goes on (the upload modal opens, the new
 *   roster opens). Its own hook starts it; the provider only remembers how it should be shown.
 * - `numbered`: one step counter across both, and Next instead of Finish on the first tour's last
 *   step while the next one is going to follow.
 * - `carriesReturn`: a Guided tours replay goes back to the list after the next tour, not this one.
 *   Off for the roster import, whose next tours sit inside the upload modal mid import.
 *
 * Each tour keeps its own progress row, so what a coach has already seen is unchanged. Mobile keeps
 * its own list in `utils/tour-engine.ts`.
 */
export interface TourContinuation {
  next: string;
  via: "chain" | "flow";
  numbered: boolean;
  carriesReturn: boolean;
}

export const TOUR_CONTINUATIONS: Readonly<Record<string, TourContinuation>> = {
  "duplicates.page": { next: "duplicates.data", via: "chain", numbered: true, carriesReturn: true },
  "roster-import.start": {
    next: "roster-import.upload-type",
    via: "flow",
    numbered: true,
    carriesReturn: false,
  },
  // The board list's last step sends the coach to make a roster; the new roster's own tour follows
  // there (`handOff`). Not numbered: the list tour has to end before the coach can make one.
  "rosters.index": { next: "rosters.new", via: "flow", numbered: false, carriesReturn: true },
};

export const continuationOf = (tourKey: string): TourContinuation | null =>
  TOUR_CONTINUATIONS[tourKey] ?? null;

const ADMIN_ROLES = new Set(["admin", "system_admin"]);

/**
 * The permission list tours should filter by. Admins and system admins always reach every page,
 * matching `hasPermission` in `utils/permissions.ts`, so their list is treated as unrestricted even
 * if a partial one were ever sent.
 */
export const permissionsForTours = (
  role: string | undefined,
  permissions: readonly string[] | undefined
): readonly string[] | undefined =>
  role && ADMIN_ROLES.has(role) ? undefined : permissions;

export const isStepRenderable = (step: TourStep, context: RenderContext): boolean => {
  if (step.anchor !== null && !context.knownAnchors.has(step.anchor)) return false;
  if (step.requiredPermission === null || context.permissions === undefined) return true;
  return context.permissions.includes(step.requiredPermission);
};

export const renderableSteps = (tour: Tour, context: RenderContext): TourStep[] =>
  tour.steps.filter((step) => isStepRenderable(step, context));

/**
 * The run a tour is due for, or null when it should not start.
 *
 * - No progress row: every renderable step.
 * - A progress row: only renderable steps not yet seen, as "new", each with the steps that open
 *   the popup it stands inside (`stepsWithTheirOpeners`).
 * - Nothing to show either way: null, and nothing is recorded.
 */
export const dueRun = (
  tour: Tour,
  progress: TourProgress | undefined,
  context: RenderContext
): TourRun | null => {
  const steps = renderableSteps(tour, context);
  if (!progress) {
    return steps.length > 0 ? { tour, steps, mode: "full", replay: false } : null;
  }
  const seen = new Set(progress.seenStepKeys);
  const unseen = new Set(steps.filter((step) => !seen.has(step.key)).map((step) => step.key));
  if (unseen.size === 0) return null;
  return { tour, steps: stepsWithTheirOpeners(steps, unseen), mode: "new", replay: false };
};

/** Every renderable step, regardless of progress. Null when there is nothing to show. */
export const replayRun = (tour: Tour, context: RenderContext): TourRun | null => {
  const steps = renderableSteps(tour, context);
  return steps.length > 0 ? { tour, steps, mode: "full", replay: true } : null;
};

/**
 * `run` without its steps on a `SKIP_WHEN_ABSENT_ANCHORS` anchor that is not on the page now.
 *
 * Asked once, as the run starts, so the step counter and the Finish button count only what will be
 * shown. The Video tour ends on "Open a video" and "Clip badge": a coach with no videos, or no
 * clips, gets the tour without those steps instead of centered cards describing a card that is not
 * there. The steps left out are not recorded as seen, so they come back as "New on this page" once
 * the page has the record. Returns the same run when nothing is left out; a run with no steps left
 * is refused by the reducer.
 *
 * A record step after a popup opener in the run is kept: it can stand inside the popup that opener
 * opens (the Schools tab's roster value sits in the roster sheet the first school opens), so it is
 * never on the page as the run starts. Leaving it out there meant it was never recorded, so the
 * tour came back as "New on this page" on every visit, with "Open a school" pulled in as its
 * opener. Such a step is decided when the run reaches it instead (`shownStepIndex` in `tourDom`
 * passes over it when its record is not there), and it is recorded as seen with the rest.
 *
 * A step on a `SKIP_WHEN_FOLDED_ANCHORS` anchor is left out too when `isFolded` says its control is only inside a
 * closed panel: on a phone the Player Database's level strip and Player type sit in the closed Filters panel, and a
 * step about them would otherwise be a centered card about controls the coach cannot see.
 */
export const withoutAbsentRecordSteps = (
  run: TourRun,
  isOnPage: (anchorId: string) => boolean,
  isFolded: (anchorId: string) => boolean = () => false
): TourRun => {
  const firstOpener = run.steps.findIndex(isPopupOpenerStep);
  const steps = run.steps.filter(
    (step, index) =>
      step.anchor === null ||
      ((!SKIP_WHEN_FOLDED_ANCHORS.has(step.anchor) || !isFolded(step.anchor)) &&
        (!SKIP_WHEN_ABSENT_ANCHORS.has(step.anchor) ||
          (firstOpener !== -1 && index > firstOpener) ||
          isOnPage(step.anchor)))
  );
  return steps.length === run.steps.length ? run : { ...run, steps };
};

export const tourKeyFor = (featureKey: string, kind: "Page" | "Data"): string =>
  `${featureKey}.${kind === "Page" ? "page" : "data"}`;

/**
 * Names the tours a page runs, when they are not the feature's own `${feature}.page` and
 * `${feature}.data`. Both are optional and default to exactly those keys.
 */
export interface TourKeyOverrides {
  /** The tour the page leads with: a Page tour (e.g. "schedule.catalog") or a FlowStep tour. */
  pageTourKey?: string;
  /** The data tour to chain when the page has data. `null` means this page has none. */
  dataTourKey?: string | null;
}

export interface ResolvedTourKeys {
  page: string;
  data: string | null;
}

export const resolveTourKeys = (
  featureKey: string,
  { pageTourKey, dataTourKey }: TourKeyOverrides = {}
): ResolvedTourKeys => ({
  page: pageTourKey ?? tourKeyFor(featureKey, "Page"),
  data: dataTourKey === undefined ? tourKeyFor(featureKey, "Data") : dataTourKey,
});

/**
 * Data tours that are a tour of their own rather than the second half of their page tour.
 *
 * "Player Profiles & Shortcuts" used to start the moment the Players tour finished, as "Step 1 of 6" straight
 * after "Step 14 of 14", which coaches read as the same tour starting over. It is never chained
 * or queued behind its page tour, and it does not auto-start on the visit that ran the page tour:
 * it waits for the next visit, and the Guided tours list has an entry for it. Mobile keeps the
 * same list in `utils/tour-engine.ts`.
 */
export const STANDALONE_DATA_TOUR_KEYS: ReadonlySet<string> = new Set(["players.data"]);

export const isStandaloneDataTour = (tourKey: string | null): boolean =>
  tourKey !== null && STANDALONE_DATA_TOUR_KEYS.has(tourKey);

export const findProgress = (
  progress: readonly TourProgress[],
  tourKey: string
): TourProgress | undefined => progress.find((entry) => entry.tourKey === tourKey);

/**
 * What to run when the page tour for a feature ends: the data tour, but only when the page has
 * data to point at. A replayed page tour chains a replayed data tour.
 */
export const chainedDataRun = (
  tours: readonly Tour[],
  progress: readonly TourProgress[],
  ended: TourRun,
  hasData: boolean,
  context: RenderContext
): TourRun | null => {
  // Only the feature's own page tour chains. A named page tour (the schedule's catalog) is a
  // different view, and its feature's data rows are not what that view shows.
  if (ended.tour.kind !== "Page" || !hasData) return null;
  if (ended.tour.key !== tourKeyFor(ended.tour.feature, "Page")) return null;
  // By key, not "the feature's first Data tour": a feature can carry a Data tour for another view
  // (the Finder detail page's `tournament-finder.teams`), and chaining that one onto the list page
  // would point every step at rows that are not there.
  const dataKey = tourKeyFor(ended.tour.feature, "Data");
  if (isStandaloneDataTour(dataKey)) return null;
  const dataTour = tours.find((tour) => tour.key === dataKey && tour.kind === "Data");
  if (!dataTour) return null;
  return ended.replay
    ? replayRun(dataTour, context)
    : dueRun(dataTour, findProgress(progress, dataTour.key), context);
};

/** Folds a finished run's steps into the progress list, as the server's union merge will. */
export const withRecordedRun = (
  progress: readonly TourProgress[],
  run: TourRun,
  outcome: TourProgress["outcome"]
): TourProgress[] => {
  const existing = findProgress(progress, run.tour.key);
  const seenStepKeys = Array.from(
    new Set([...(existing?.seenStepKeys ?? []), ...run.steps.map((step) => step.key)])
  );
  const updated: TourProgress = { tourKey: run.tour.key, outcome, seenStepKeys };
  return existing
    ? progress.map((entry) => (entry.tourKey === run.tour.key ? updated : entry))
    : [...progress, updated];
};

/** Whether a feature has any tour with at least one renderable step. */
export const featureHasTours = (
  tours: readonly Tour[],
  featureKey: string,
  context: RenderContext
): boolean =>
  tours.some(
    (tour) => tour.feature === featureKey && renderableSteps(tour, context).length > 0
  );
